import React from 'react';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';

// TODO Style header similar to Step3HasSamples.tsx?
const HeaderTableCell = styled(TableCell)(({theme}) => ({
    backgroundColor: theme.palette.surfaceContainerHigh,
    color: theme.palette.onSurface,
    fontWeight: 'bold'
}));

// NOTE: This component is currently in step3, but might replace step 4 for
// chip. Just that we would prefer the user to review things before uploading.

interface ChipSample {
    SampleName: string;
    Replicate: string | number;
    Factor: string;
    Tissue: string;
    Condition: string;
    Treatment: string;
    SampleType: string;
    ControlName: string;
}

interface ChipSamplesTableProps {
    samples: ChipSample[];
    samplenameToFilenames: Record<string, string[]>;
    controlColumn?: boolean;
    sampleTypeColumn?: boolean;
}

/**
 * Sorts samples by metadata and replicate column so replicates will be next to
 * each other.
 *
 * Does not use groupAndSortSamplesByReplicates() because it contains unneeded
 * validation. Perhaps refactor that function to separate validation and
 * grouping/sorting so we can reuse here.
 */
const sortSamplesByReplicateGroups = (samples: ChipSample[]): ChipSample[] => {
    const groupedSamples: Record<string, ChipSample[]> = {};

    samples.forEach(sample => {
        const key = `${sample.Factor}_${sample.Tissue}_${sample.Condition}_${sample.Treatment}`;
        if (groupedSamples[key] === undefined) {
            groupedSamples[key] = [];
        }
        groupedSamples[key].push(sample);
    });

    // Sort within each group by replicate number
    const sortedGroups = Object.values(groupedSamples).map(group => group.sort((a, b) => Number(a.Replicate) - Number(b.Replicate)));
    // Sort groups by first sample's name
    sortedGroups.sort((a, b) => a[0].SampleName.localeCompare(b[0].SampleName));

    return sortedGroups.flat();
};

/**
 * Single table of ChIP samples.
 * Control Column and sampleType column can be excluded
 */
export const ChipSamplesTable = ({
    samples,
    samplenameToFilenames,
    controlColumn = true,
    sampleTypeColumn = true
}: ChipSamplesTableProps) => {
    if (samples.length === 0) {
        // Should not be reachable by the way we call this component.
        return null;
    }
    const sortedSamples = sortSamplesByReplicateGroups(samples);
    return (
        <TableContainer sx={{maxHeight: '25em'}}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <HeaderTableCell>Sample Name</HeaderTableCell>
                        <HeaderTableCell align="right">Replicate</HeaderTableCell>
                        <HeaderTableCell>Factor</HeaderTableCell>
                        <HeaderTableCell>Tissue</HeaderTableCell>
                        <HeaderTableCell>Condition</HeaderTableCell>
                        <HeaderTableCell>Treatment</HeaderTableCell>
                        {sampleTypeColumn ? <HeaderTableCell>SampleType</HeaderTableCell> : null}
                        {controlColumn ? <HeaderTableCell>ControlName</HeaderTableCell> : null}
                        <HeaderTableCell>Files</HeaderTableCell>
                        {controlColumn ? <HeaderTableCell>Control Files</HeaderTableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedSamples.map(sample => (
                        <TableRow key={sample.SampleName}>
                            <TableCell>{sample.SampleName}</TableCell>
                            <TableCell align="right">{sample.Replicate}</TableCell>
                            <TableCell>{sample.Factor}</TableCell>
                            <TableCell>{sample.Tissue}</TableCell>
                            <TableCell>{sample.Condition}</TableCell>
                            <TableCell>{sample.Treatment}</TableCell>
                            {sampleTypeColumn ? <TableCell>{sample.SampleType}</TableCell> : null}
                            {controlColumn ? <TableCell>{sample.ControlName}</TableCell> : null}
                            <TableCell>
                                {samplenameToFilenames[sample.SampleName]?.map((filename, index) => <div key={index}>{filename};</div>)}
                            </TableCell>
                            {controlColumn ? (
                                <TableCell>
                                    {samplenameToFilenames[sample.ControlName]?.map((filename, index) => (
                                        <div key={index}>{filename};</div>
                                    ))}
                                </TableCell>
                            ) : null}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

interface ChipSamplesTablesProps {
    samples: ChipSample[];
    samplenameToFilenames: Record<string, string[]>;
}

/**
 * Shows up to three tables of samples: Control (Input), IP with control, and IP without control.
 */
export const ChipSamplesTables = ({samples, samplenameToFilenames}: ChipSamplesTablesProps) => {
    const controlSamples = samples.filter(sample => sample.SampleType === 'Input');
    const ipControl = samples.filter(sample => sample.SampleType === 'IP' && sample.ControlName !== 'NoControl');
    const ipNoControl = samples.filter(sample => sample.SampleType === 'IP' && sample.ControlName === 'NoControl');

    let Controls = null;
    let IP = null;
    let NoControl = null;
    if (controlSamples.length > 0) {
        Controls = (
            <>
                <Typography variant="h4" sx={{my: 1}}>
                    Controls
                </Typography>
                <ChipSamplesTable samples={controlSamples} samplenameToFilenames={samplenameToFilenames} controlColumn={false} />
            </>
        );
    }
    if (ipControl.length > 0) {
        IP = (
            <>
                <Typography variant="h4" sx={{my: 1}}>
                    IP with Control
                </Typography>
                <ChipSamplesTable samples={ipControl} samplenameToFilenames={samplenameToFilenames} />
            </>
        );
    }
    if (ipNoControl.length > 0) {
        NoControl = (
            <>
                <Typography variant="h4" sx={{my: 1}}>
                    IP without Control
                </Typography>
                <ChipSamplesTable samples={ipNoControl} samplenameToFilenames={samplenameToFilenames} controlColumn={false} />
            </>
        );
    }

    return (
        <>
            {Controls}
            {IP}
            {NoControl}
        </>
    );
};
